/**
 * KatieChatWidget · v2.7.65+E106
 * ══════════════════════════════════════════════════════════════════
 * Public-facing rider concierge. Named after the wife of Steven
 * Buller (CEO/GP). Mounted on every marketing page via CollapsibleChatPanel.
 *
 * Capabilities:
 *   • Answer FAQ from the public knowledge base (audience=public).
 *   • Streaming replies via SSE from POST /api/customer-chat/chat/stream.
 *   • Take a booking (opens an inline mini-form) → POST /api/customer-chat/booking.
 *   • Track "where is my driver?" → GET  /api/customer-chat/my-ride.
 *   • Open a support ticket → POST /api/customer-chat/ticket.
 *
 * Data-isolation notes: this widget NEVER sends an admin JWT. Rider
 * identity is optional (name/phone/email) and stored per-session in
 * localStorage only. Rate-limited server-side to 10 msgs/hour/IP.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, User, Phone, MapPin, Ticket, Car,
         ExternalLink, Globe, Clock, MessageSquareOff } from "lucide-react";
import CollapsibleChatPanel from "./CollapsibleChatPanel";
import usePortalHours from "@/hooks/usePortalHours";
import ReopenCountdown from "@/components/ReopenCountdown";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const SESSION_KEY = "katie_session_id_v1";
const RIDER_KEY   = "katie_rider_v1";
// v2.7.65+E156 · Rebranded to "Buller Taxi Support" with the official
// company crest as the chat avatar. Display strings below flow to all
// visible header / launcher / typing indicators. Function names stay
// (`KatieAvatar`, `KatieBubble`) as internal identifiers — the wire
// endpoints `/api/customer-chat/*` remain unchanged so history, queue
// status, escalation, translations, quick actions, live-agent handoff
// and rider-identity syncing all continue exactly as before.
const DISPLAY_NAME     = "Buller Taxi Support";
const DISPLAY_SUBTITLE = "Buller Taxi · Support";
const KATIE_PHOTO_URL  = "/buller-taxi-crest-avatar.png";

// Portrait of Katie (wife of Steven Buller, CEO/GP) sitting in her call-
// center office with a headset. Object-covers the round frame so the
// centred subject stays visible at every launcher size.
function KatieAvatar({ size = "h-10 w-10" }) {
  return (
    <div className={`${size} rounded-full overflow-hidden border-2 border-amber-400/60 bg-zinc-900`}>
      <img
        src={KATIE_PHOTO_URL}
        alt={DISPLAY_NAME}
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover"
        data-testid="katie-avatar-img"
      />
    </div>
  );
}

// v2.7.65+E108 · Trigger phrases that flip Katie from AI-first to
// live-agent handoff. When any of these fire, Katie posts to
// /api/customer-chat/escalate and enters "live_agent" mode — subsequent
// user turns land in the human queue, and queue position polls.
const HANDOFF_TRIGGERS = /\b(human|agent|person|representative|supervisor|manager|refund|complain(?:t|ing)?|escalate|talk to (?:a|someone))\b/i;

export default function KatieChatWidget() {
  const [rider, setRider] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RIDER_KEY) || "{}"); }
    catch { return {}; }
  });
  // v2.7.65+E107 · Listen for cross-tab (and same-tab dispatched) storage
  // events so Katie picks up rider identity the moment they type it into
  // the homepage Quick Booking form — even before she's been opened.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== RIDER_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        setRider((prev) => ({ ...prev, ...parsed }));
      } catch { /* silent */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || ""; }
    catch { return ""; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [quickForm, setQuickForm] = useState(null); // 'booking' | 'ticket' | null
  // v2.8+E304 · Live Hours-of-Operation gate. When the business is CLOSED
  // the chat is shown as INACTIVE (unavailable message + hours) instead of
  // interactive — and flips back online automatically when hours resume
  // (usePortalHours polls every 20s + subscribes to the rider-hours WS).
  const hours = usePortalHours();
  const closed = !!(hours && hours.is_open === false);
  // v2.7.65+E108 · Live-agent handoff state
  const [liveMode, setLiveMode] = useState(false);      // true = talking to human via /chat
  const [liveSessionId, setLiveSessionId] = useState(null);
  const [queue, setQueue] = useState(null);   // {position, queue_size, assigned_to, avg_wait_min}
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const liveMsgSeenRef = useRef(new Set());  // dedupe polling
  const queueTimerRef = useRef(null);
  // v2.7.65+E156 · Streaming perf
  // • `sendingRef` — synchronous double-submit guard (React state updates
  //   are async; a fast double-Enter used to slip past the `busy` check).
  // • `bufferRef`  — accumulates SSE token frames off-state.
  // • `flushRafRef`— rAF handle that flushes `bufferRef` into the visible
  //   assistant bubble on the next paint (≤ 60fps). Cuts per-token React
  //   re-renders from 20-50/sec down to ~60/sec regardless of stream rate.
  // • `abortRef`   — AbortController for the in-flight fetch; cancels on
  //   unmount or when the user fires a new send, killing zombie streams.
  const sendingRef  = useRef(false);
  const bufferRef   = useRef("");
  const flushRafRef = useRef(null);
  const abortRef    = useRef(null);
  const streamIdxRef = useRef(-1);
  useEffect(() => () => {
    if (flushRafRef.current) cancelAnimationFrame(flushRafRef.current);
    if (abortRef.current)    { try { abortRef.current.abort(); } catch { /* noop */ } }
  }, []);

  // Persist rider bag / session_id whenever they change.
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem(SESSION_KEY, sessionId);
    } catch { /* private-mode */ }
  }, [sessionId]);
  useEffect(() => {
    try { localStorage.setItem(RIDER_KEY, JSON.stringify(rider || {})); }
    catch { /* private-mode */ }
  }, [rider]);

  // Fetch persona greeting on mount so the first bubble is warm + personal.
  useEffect(() => {
    (async () => {
      try {
        const url = new URL(`${BASE}/api/customer-chat/greeting`);
        if (rider?.email)      url.searchParams.set("email",      rider.email);
        if (rider?.first_name) url.searchParams.set("first_name", rider.first_name);
        const r = await fetch(url.toString());
        const j = await r.json();
        setGreeting(j?.greeting || "");
      } catch {
        setGreeting("Welcome to Buller Taxi. Ready when you are — where are we headed?");
      }
    })();
  }, [rider?.email, rider?.first_name]);

  // Seed the welcome message the first time the panel opens.
  // v2.7.65+E118 · Remembered language chip · lets the rider see + opt-out
  const [prefLang, setPrefLang] = useState(() => {
    try { return window.localStorage.getItem("katie_pref_lang") || ""; }
    catch { return ""; }
  });
  const LANG_NATIVE = {
    en: "English", es: "Español", ja: "日本語", vi: "Tiếng Việt",
    zh: "中文", de: "Deutsch", ru: "Русский",
  };
  const clearPrefLang = () => {
    try { window.localStorage.removeItem("katie_pref_lang"); }
    catch { /* quota */ }
    setPrefLang("");
  };

  useEffect(() => {
    if (!panelOpen || messages.length > 0) return;
    const opener = greeting || "Welcome to Buller Taxi. Ready when you are — where are we headed?";
    setMessages([{ role: "assistant", content: opener, at: new Date().toISOString() }]);
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [panelOpen, messages.length, greeting]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  // ─── Escalate to a live agent ─────────────────────────────────────
  const escalateToLiveAgent = useCallback(async (reason, lastMsg) => {
    try {
      // v2.7.65+E116 · Try to recover the rider's remembered chat
      // language BEFORE the escalate call so the very first exchange
      // opens in the right language.
      let rememberedLang = null;
      try {
        const local = window.localStorage.getItem("katie_pref_lang");
        if (local && local.length === 2) rememberedLang = local;
      } catch { /* quota */ }
      if (!rememberedLang && (rider?.phone || rider?.email)) {
        try {
          const lookup = await fetch(`${BASE}/api/customer-chat/rider-language/lookup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone: rider?.phone, email: rider?.email }),
          });
          if (lookup.ok) {
            const lj = await lookup.json();
            if (lj?.found && lj?.lang) rememberedLang = lj.lang;
          }
        } catch { /* network — non-fatal */ }
      }

      const resp = await fetch(`${BASE}/api/customer-chat/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          katie_session_id:   sessionId || undefined,
          first_name:         rider?.first_name,
          phone:              rider?.phone,
          email:              rider?.email,
          last_user_message:  lastMsg || "",
          reason,
          customer_lang:      rememberedLang || undefined,
        }),
      });
      if (!resp.ok) throw new Error(`Escalate ${resp.status}`);
      const j = await resp.json();
      setLiveSessionId(j.session_id);
      setLiveMode(true);
      setQueue({
        position:      j.queue_position,
        queue_size:    j.queue_size,
        assigned_to:   null,
        avg_wait_min:  Math.max(1, (j.queue_position || 1) * 3),
      });
      setMessages((m) => [...m, {
        role:      "assistant",
        content:   `Passing you to a Buller Taxi teammate now. You are #${j.queue_position} in line — a live agent will pick up shortly. You can keep typing here and they'll see everything.`,
        at:        new Date().toISOString(),
        source_badge: "handoff",
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant", error: true,
        content: `I tried to hand you off to a person but couldn't reach the team — please call (580) 219-4687. Reason: ${e.message}`,
        at: new Date().toISOString(),
      }]);
    }
  }, [BASE, sessionId, rider]);

  // Poll queue status + new agent messages while in live mode.
  useEffect(() => {
    if (!liveMode || !liveSessionId) return undefined;
    const tick = async () => {
      try {
        const qResp = await fetch(`${BASE}/api/chat/queue-status?session_id=${liveSessionId}`);
        if (qResp.ok) {
          const q = await qResp.json();
          setQueue({
            position:     q.position,
            queue_size:   q.queue_size,
            assigned_to:  q.assigned_to,
            avg_wait_min: q.avg_wait_min,
          });
        }
        // v2.7.65+E117 · Fetch staff replies via the token-less Katie
        // read endpoint (matches the send endpoint). The old path
        // required a customer_token that Katie-escalated sessions do
        // not mint, so polling used to silently 422 — cutting off the
        // outbound (agent→customer) sync entirely.
        const mResp = await fetch(`${BASE}/api/customer-chat/sessions/${liveSessionId}/messages`);
        if (mResp.ok) {
          const jm = await mResp.json();
          for (const item of (jm.items || [])) {
            if (item.direction !== "staff") continue;
            if (liveMsgSeenRef.current.has(item.id)) continue;
            liveMsgSeenRef.current.add(item.id);
            setMessages((m) => [...m, {
              role:    "assistant",
              content: item.text || "",
              at:      item.created_at,
              agent_name: item.sender_name,
            }]);
          }
        }
      } catch { /* silent — next tick will retry */ }
    };
    // Fire once immediately then poll every 4s while in live mode.
    tick();
    queueTimerRef.current = setInterval(tick, 4000);
    return () => { if (queueTimerRef.current) clearInterval(queueTimerRef.current); };
  }, [liveMode, liveSessionId, BASE]);

  // ─── Streaming send ────────────────────────────────────────────────
  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text) return;
    // v2.7.65+E156 · Synchronous double-submit guard — React state
    // updates are async so `busy` can lag by a frame. `sendingRef`
    // slams the gate on the same tick.
    if (sendingRef.current || busy) return;
    sendingRef.current = true;
    setMessages((m) => [...m, { role: "user", content: text, at: new Date().toISOString() }]);
    setInput("");

    // v2.7.65+E115 · Live-agent path — hits the token-less Katie
    // customer send endpoint that runs server-side language detection
    // and pushes both the original + translated English into the admin
    // console. `lang: "auto"` opts in explicitly; the server can also
    // detect from the raw text if we omit it.
    if (liveMode && liveSessionId) {
      try {
        const resp = await fetch(`${BASE}/api/customer-chat/sessions/${liveSessionId}/messages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sender_id:   sessionId || "rider",
            sender_name: rider?.first_name || "Rider",
            text,
            lang:        "auto",
          }),
        });
        if (!resp.ok) throw new Error(`Send ${resp.status}`);
        // v2.7.65+E116 · Cache the detected language locally so a
        // returning rider gets their preferred language immediately on
        // next visit — even before we round-trip to the lookup endpoint.
        try {
          const rj = await resp.json();
          const l = rj?.auto_detected_lang || rj?.lang;
          if (l && l.length === 2) {
            window.localStorage.setItem("katie_pref_lang", l);
          }
        } catch { /* quota */ }
      } catch (e) {
        setMessages((m) => [...m, { role: "assistant", error: true,
          content: `Couldn't send that message to the live agent — ${e.message}. Call (405) 805-0199.`,
          at: new Date().toISOString() }]);
      }
      sendingRef.current = false;
      return;
    }

    // v2.7.65+E108 · Katie AI path — but first check for handoff triggers.
    // If the rider is asking for a human, don't waste an LLM call — hand
    // off immediately.
    if (HANDOFF_TRIGGERS.test(text)) {
      sendingRef.current = false;
      await escalateToLiveAgent("trigger_phrase", text);
      return;
    }

    setBusy(true);

    // Placeholder assistant bubble filled in as tokens arrive.
    const assistantIndex = { current: -1 };
    setMessages((m) => {
      assistantIndex.current = m.length;
      streamIdxRef.current   = m.length;
      return [...m, { role: "assistant", content: "", at: new Date().toISOString(), streaming: true }];
    });

    // v2.7.65+E156 · rAF-throttled bubble flusher. Runs at most once per
    // frame regardless of how many tokens land in that frame.
    bufferRef.current = "";
    const scheduleFlush = () => {
      if (flushRafRef.current) return;
      flushRafRef.current = requestAnimationFrame(() => {
        flushRafRef.current = null;
        const idx = streamIdxRef.current;
        if (idx < 0) return;
        const next = bufferRef.current;
        setMessages((m) => {
          if (!m[idx]) return m;
          if (m[idx].content === next) return m;   // ↓ avoid pointless re-render
          const copy = m.slice();
          copy[idx] = { ...copy[idx], content: next };
          return copy;
        });
      });
    };

    // v2.7.65+E156 · Cancel any prior in-flight stream before starting a
    // new one (defensive — the double-submit guard normally prevents
    // this, but a background reconnect could still race).
    if (abortRef.current) { try { abortRef.current.abort(); } catch { /* noop */ } }
    const ac = new AbortController();
    abortRef.current = ac;

    let finalText = "";
    try {
      const resp = await fetch(`${BASE}/api/customer-chat/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
        body: JSON.stringify({
          message:    text,
          session_id: sessionId || undefined,
          first_name: rider?.first_name || undefined,
          phone:      rider?.phone      || undefined,
          email:      rider?.email      || undefined,
        }),
        signal: ac.signal,
      });
      if (!resp.ok || !resp.body) {
        if (resp.status === 429) {
          throw new Error("You've reached the message limit for this hour. Please call us at (405) 805-0199.");
        }
        throw new Error(`Server error · ${resp.status}`);
      }
      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const lines     = frame.split("\n").filter(Boolean);
          const eventLine = lines.find((l) => l.startsWith("event:"));
          const dataLine  = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const event = (eventLine || "event: message").slice(6).trim();
          const raw   = dataLine.slice(5).trim();
          let parsed;
          try { parsed = JSON.parse(raw); } catch { parsed = raw; }
          if (event === "meta" && parsed?.session_id) {
            setSessionId(parsed.session_id);
            if (parsed?.sources) {
              setMessages((m) => {
                const copy = m.slice();
                const idx = assistantIndex.current;
                if (copy[idx]) copy[idx] = { ...copy[idx], sources: parsed.sources };
                return copy;
              });
            }
          } else if (event === "token" && typeof parsed === "string") {
            finalText += parsed;
            bufferRef.current = finalText;
            scheduleFlush();
          } else if (event === "done") {
            // Final flush → guarantee last token lands even if rAF was
            // still pending when the stream closed.
            if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
            setMessages((m) => {
              const copy = m.slice();
              const idx = assistantIndex.current;
              if (copy[idx]) copy[idx] = {
                ...copy[idx],
                content:   bufferRef.current || copy[idx].content,
                streaming: false,
                sources:   parsed?.sources || copy[idx].sources,
              };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      // Ignore user-triggered aborts (silent — they superseded this send).
      if (e?.name !== "AbortError") {
        setMessages((m) => {
          const copy = m.slice();
          const idx = assistantIndex.current;
          const errText = e?.message || "Sorry — I couldn't reach our server.";
          if (copy[idx]) copy[idx] = { role: "assistant", content: errText, at: new Date().toISOString(), error: true };
          return copy;
        });
      }
    } finally {
      if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
      streamIdxRef.current = -1;
      setBusy(false);
      sendingRef.current = false;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, busy, sessionId, rider, liveMode, liveSessionId, escalateToLiveAgent]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ─── Quick-actions (booking / ticket) ────────────────────────────────
  const submitBooking = useCallback(async (form) => {
    try {
      const resp = await fetch(`${BASE}/api/customer-chat/booking`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...form, session_id: sessionId || undefined }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `Booking failed · ${resp.status}`);
      }
      const j = await resp.json();
      setRider((r) => ({ ...r, first_name: form.first_name, phone: form.phone }));
      if (j?.session_id) setSessionId(j.session_id);
      toast.success("Ride booked! Dispatch will call you back to confirm.");
      setQuickForm(null);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Booked! Ride ID **${j.ride_id.slice(0, 8)}…** is now in the dispatch queue. Our team will call ${form.phone} shortly to confirm your driver.`,
        at: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(e.message || "Booking failed");
    }
  }, [sessionId]);

  const submitTicket = useCallback(async (form) => {
    try {
      const resp = await fetch(`${BASE}/api/customer-chat/ticket`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...form, session_id: sessionId || undefined }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `Ticket failed · ${resp.status}`);
      }
      const j = await resp.json();
      toast.success("Ticket opened — we'll be in touch within 24 hours.");
      setQuickForm(null);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Thanks — I've opened ticket **${j.ticket_id.slice(0, 8)}…** (${j.priority}). Our team will follow up with you within 24 hours.`,
        at: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(e.message || "Ticket failed");
    }
  }, [sessionId]);

  const launcher = useMemo(() => (
    <div className="relative">
      <div className="h-10 w-10 rounded-full overflow-hidden border-2 border-amber-400/60">
        <KatieAvatar size="h-full w-full"/>
      </div>
      <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-900 ${closed ? "bg-zinc-500" : "bg-emerald-400 animate-pulse"}`}/>
    </div>
  ), [closed]);

  return (
    <CollapsibleChatPanel
      title={DISPLAY_NAME}
      variant="floating"
      accent="amber"
      triggerLabel={DISPLAY_NAME}
      triggerSubLabel={closed ? "Currently closed" : "Book · rates · lost items"}
      triggerAvatar={launcher}
      statusDot={closed ? "bg-zinc-500" : "bg-emerald-400 animate-pulse"}
      onOpenChange={setPanelOpen}
    >
      {closed ? (
        <ClosedChatPanel hours={hours} />
      ) : (
      <div className="flex flex-col h-full min-h-0">
        {/* Persona header · sits INSIDE the panel below the shell header. */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-amber-500/10 via-zinc-950 to-zinc-950">
          <div className="relative flex-shrink-0">
            <KatieAvatar size="h-14 w-14"/>
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-900 bg-emerald-400 animate-pulse"
                  title={`${DISPLAY_NAME} is online`}/>
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-zinc-100">{DISPLAY_NAME}</div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
              {DISPLAY_SUBTITLE}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Book · rates · lost items · lost & found
            </div>
          </div>
        </div>

        {/* Message list */}
        <div ref={listRef}
             data-testid="katie-message-list"
             className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-950">
          {/* v2.7.65+E118 · Remembered language chip · gives the rider
              a friendly opt-out for the auto-language pick. */}
          {prefLang && prefLang !== "en" && !liveMode && (
            <div
              data-testid="katie-lang-chip"
              className="mx-auto text-[11px] rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-1 inline-flex items-center gap-1.5 max-w-fit"
            >
              <Globe className="h-3 w-3"/>
              Continuing in <b>{LANG_NATIVE[prefLang] || prefLang.toUpperCase()}</b>
              <button
                type="button"
                onClick={clearPrefLang}
                data-testid="katie-lang-chip-switch"
                className="ml-1 uppercase tracking-widest font-bold text-amber-300 hover:text-amber-100 underline-offset-2 hover:underline"
              >
                Switch
              </button>
            </div>
          )}
          {/* E108 · Live-agent queue banner. Renders only in live mode. */}
          {liveMode && queue && (
            <div className={`rounded-lg border p-3 text-xs ${
              queue.assigned_to
                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200"
                : "bg-amber-500/10 border-amber-500/40 text-amber-200"
            }`}
                 data-testid="katie-queue-banner"
                 data-assigned={queue.assigned_to ? "1" : "0"}>
              {queue.assigned_to ? (
                <>
                  <span className="font-bold uppercase tracking-widest text-[10px] text-emerald-300 block mb-0.5">
                    Live agent connected
                  </span>
                  You&apos;re chatting with <b>{queue.assigned_to}</b> now.
                </>
              ) : (
                <>
                  <span className="font-bold uppercase tracking-widest text-[10px] text-amber-300 block mb-0.5">
                    In queue for a live agent
                  </span>
                  You are <b>#{queue.position || 1}</b> in line ·
                  {" "}~<b>{queue.avg_wait_min || 3} min</b> wait ·
                  {" "}{queue.queue_size} rider{queue.queue_size === 1 ? "" : "s"} ahead of you.
                </>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <KatieBubble key={i} msg={m}/>
          ))}
          {busy && !messages.some((m) => m.streaming) && (
            <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
              <Loader2 className="h-3 w-3 animate-spin"/>{DISPLAY_NAME} is typing…
            </div>
          )}
        </div>

        {/* Quick-action mini forms */}
        {quickForm === "booking" && (
          <BookingForm
            defaults={rider}
            onCancel={() => setQuickForm(null)}
            onSubmit={submitBooking}
          />
        )}
        {quickForm === "ticket" && (
          <TicketForm
            defaults={rider}
            onCancel={() => setQuickForm(null)}
            onSubmit={submitTicket}
          />
        )}

        {/* Quick-action toolbar */}
        <div className="flex items-center gap-1 px-3 pt-2 border-t border-zinc-800 bg-zinc-950">
          <QuickBtn onClick={() => setQuickForm("booking")}
                    icon={<Car className="h-3 w-3"/>}
                    label="Book a ride"
                    testid="katie-quick-booking"/>
          <QuickBtn onClick={() => send("Where is my driver? My phone is " + (rider?.phone || ""))}
                    disabled={!rider?.phone}
                    icon={<MapPin className="h-3 w-3"/>}
                    label="My ride status"
                    testid="katie-quick-status"/>
          <QuickBtn onClick={() => setQuickForm("ticket")}
                    icon={<Ticket className="h-3 w-3"/>}
                    label="Open a ticket"
                    testid="katie-quick-ticket"/>
          <QuickBtn onClick={() => escalateToLiveAgent("manual",
                                     "The rider requested a human agent.")}
                    disabled={liveMode}
                    icon={<User className="h-3 w-3"/>}
                    label={liveMode ? "Live agent" : "Talk to a person"}
                    testid="katie-quick-human"/>
        </div>

        {/* Input */}
        <div className="border-t border-zinc-800 bg-zinc-900 px-3 py-2">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="katie-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Ask about rates, book a ride, or say hi…"
              className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-amber-400 resize-none max-h-32"
              style={{ minHeight: "38px" }}
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="katie-send"
              className="h-9 px-3 rounded-lg bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              aria-label="Send"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Send className="h-3.5 w-3.5"/>}
            </button>
          </div>
        </div>
        <div
          className="text-center text-[10px] text-zinc-600 py-1.5 border-t border-zinc-800 bg-zinc-950"
          data-testid="katie-footer"
        >
          Live Agent{" "}
          <a
            href="tel:+15802194687"
            className="text-amber-400 hover:text-amber-300 underline-offset-2 hover:underline"
            data-testid="katie-footer-phone"
          >
            (580) 219-4687
          </a>
          {" · "}Email:{" "}
          <a
            href="mailto:dispatch@bullertaxi.com"
            className="text-amber-400 hover:text-amber-300 underline-offset-2 hover:underline"
            data-testid="katie-footer-email"
          >
            dispatch@bullertaxi.com
          </a>
        </div>
      </div>
      )}
    </CollapsibleChatPanel>
  );
}

// ─── Closed / after-hours panel ───────────────────────────────────────
// Shown when the business is outside operating hours. Keeps the widget
// present (launcher stays) but replaces the interactive chat with an
// "unavailable" message + live business hours + when chat reopens.
function ClosedChatPanel({ hours }) {
  const time = (hours?.hours_label || "10:00 AM – 3:00 AM").replace(/\s*(CDT|CST)\b/i, "").trim();
  const days = hours?.days_label || "7 days a week";
  const tz = hours?.tz_abbrev || "CDT";
  const nextOpen = hours?.next_open_label;
  const nextOpenAt = hours?.next_open_at;
  return (
    <div className="flex flex-col h-full min-h-0 bg-zinc-950" data-testid="katie-closed-panel">
      {/* Offline persona header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-amber-500/10 via-zinc-950 to-zinc-950">
        <div className="relative flex-shrink-0">
          <div className="h-14 w-14 rounded-full overflow-hidden border-2 border-amber-400/40 opacity-80">
            <img src={KATIE_PHOTO_URL} alt={DISPLAY_NAME} className="h-full w-full object-cover grayscale" draggable={false}/>
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-900 bg-zinc-500"/>
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-zinc-100">{DISPLAY_NAME}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold" data-testid="katie-closed-status">
            ● Offline · Support chat closed
          </div>
        </div>
      </div>

      {/* Unavailable message + live hours */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-7 flex flex-col items-center text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-amber-400/10 border border-amber-400/40 flex items-center justify-center mb-4">
          <MessageSquareOff className="h-8 w-8 text-amber-300"/>
        </div>
        <h3 className="text-lg font-extrabold text-zinc-100" data-testid="katie-closed-heading">
          Support Chat Unavailable
        </h3>
        <p className="text-sm text-zinc-400 mt-2 max-w-xs leading-relaxed">
          Our live support chat is offline right now. It comes back online automatically during our business hours.
        </p>

        <div className="mt-5 w-full max-w-xs rounded-xl bg-zinc-900 border border-zinc-800 p-4 text-left">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-black mb-2">
            <Clock className="h-3.5 w-3.5"/> Hours of Operation
          </div>
          <div className="text-sm font-bold text-zinc-100" data-testid="katie-closed-hours">{days}, {time}</div>
          <div className="text-xs text-zinc-500 mt-0.5">Central Time ({tz})</div>
          {nextOpen && (
            <div className="mt-3 pt-3 border-t border-zinc-800 text-xs" data-testid="katie-closed-nextopen">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0 animate-pulse"/>
                <span className="text-zinc-300">Chat reopens <b className="text-emerald-300">{nextOpen}</b></span>
              </div>
              <ReopenCountdown
                nextOpenAt={nextOpenAt}
                testId="katie-reopen-countdown"
                prefix="reopens in "
                className="mt-1.5 inline-block rounded-md bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-emerald-300 font-bold tracking-wide"
              />
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-zinc-500">Need a ride now? Call dispatch:</div>
        <a
          href="tel:+15802194687"
          data-testid="katie-closed-call"
          className="mt-1 inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-bold"
        >
          <Phone className="h-4 w-4"/> (580) 219-4687
        </a>
      </div>
    </div>
  );
}

// ─── Message bubble ────────────────────────────────────────────────────
function KatieBubble({ msg }) {
  const isUser = msg.role === "user";
  const isStreaming = msg.streaming && !isUser;
  const sources = Array.isArray(msg.sources) ? msg.sources : [];
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? "bg-amber-400 text-zinc-950 rounded-br-sm"
            : msg.error
              ? "bg-rose-950/40 border border-rose-700 text-rose-200 rounded-bl-sm"
              : "bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-bl-sm"
        }`}
        data-testid={isUser ? "katie-msg-user" : "katie-msg-assistant"}
        data-streaming={isStreaming ? "1" : "0"}
      >
        {msg.content}
        {isStreaming && (
          <span className="inline-block w-1.5 h-3 ml-0.5 bg-amber-400 align-middle animate-pulse"/>
        )}
        {sources.length > 0 && !isUser && !msg.error && (
          <div className="mt-2 pt-2 border-t border-zinc-800/60">
            <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-500 mb-1">
              From our help center
            </div>
            <div className="flex flex-wrap gap-1">
              {sources.slice(0, 4).map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded"
                  data-testid={`katie-citation-${i}`}
                >
                  {(s.name || "source").replace(/^KB · /, "").slice(0, 46)}
                  <ExternalLink className="h-2.5 w-2.5 opacity-60"/>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Booking mini-form ────────────────────────────────────────────────
function BookingForm({ defaults, onCancel, onSubmit }) {
  const [f, setF] = useState({
    first_name:      defaults?.first_name || "",
    phone:           defaults?.phone      || "",
    pickup_address:  "",
    dropoff_address: "",
    pickup_time:     "ASAP",
    notes:           "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.first_name || !f.phone || !f.pickup_address || !f.dropoff_address) {
      toast.error("Please fill name, phone, and both addresses.");
      return;
    }
    setSaving(true);
    await onSubmit(f);
    setSaving(false);
  };
  return (
    <div className="border-t border-amber-500/40 bg-amber-500/5 px-3 py-3 space-y-2"
         data-testid="katie-booking-form">
      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 flex items-center gap-1">
        <Car className="h-3 w-3"/>Book a Ride
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniInput icon={<User className="h-3 w-3"/>} value={f.first_name} onChange={set("first_name")} placeholder="Your first name"/>
        <MiniInput icon={<Phone className="h-3 w-3"/>} value={f.phone} onChange={set("phone")} placeholder="Phone"/>
      </div>
      <MiniInput icon={<MapPin className="h-3 w-3"/>} value={f.pickup_address} onChange={set("pickup_address")} placeholder="Pickup address"/>
      <MiniInput icon={<MapPin className="h-3 w-3"/>} value={f.dropoff_address} onChange={set("dropoff_address")} placeholder="Drop-off address"/>
      <div className="grid grid-cols-2 gap-2">
        <MiniInput value={f.pickup_time} onChange={set("pickup_time")} placeholder="ASAP or e.g. 2:30pm"/>
        <MiniInput value={f.notes} onChange={set("notes")} placeholder="Notes (optional)"/>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel}
                className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-200 px-2 py-1">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={saving}
                data-testid="katie-booking-submit"
                className="text-[11px] font-bold uppercase tracking-widest bg-amber-400 hover:bg-amber-300 text-zinc-950 rounded px-3 py-1 disabled:opacity-50">
          {saving ? <Loader2 className="h-3 w-3 animate-spin inline"/> : "Send to dispatch"}
        </button>
      </div>
    </div>
  );
}

// ─── Ticket mini-form ─────────────────────────────────────────────────
function TicketForm({ defaults, onCancel, onSubmit }) {
  const [f, setF] = useState({
    first_name: defaults?.first_name || "",
    phone:      defaults?.phone      || "",
    email:      defaults?.email      || "",
    topic:      "complaint",
    message:    "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.first_name || !f.phone || !f.message || f.message.length < 5) {
      toast.error("Please fill name, phone, and tell us what happened.");
      return;
    }
    setSaving(true);
    await onSubmit(f);
    setSaving(false);
  };
  return (
    <div className="border-t border-rose-500/40 bg-rose-500/5 px-3 py-3 space-y-2"
         data-testid="katie-ticket-form">
      <div className="text-[10px] uppercase tracking-widest font-bold text-rose-300 flex items-center gap-1">
        <Ticket className="h-3 w-3"/>Open a Support Ticket
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniInput icon={<User className="h-3 w-3"/>} value={f.first_name} onChange={set("first_name")} placeholder="Your name"/>
        <MiniInput icon={<Phone className="h-3 w-3"/>} value={f.phone} onChange={set("phone")} placeholder="Phone"/>
      </div>
      <MiniInput value={f.email} onChange={set("email")} placeholder="Email (optional)"/>
      <select value={f.topic} onChange={set("topic")}
              className="w-full bg-zinc-900 border border-zinc-700 rounded h-9 px-2 text-sm text-zinc-100"
              data-testid="katie-ticket-topic">
        <option value="complaint">Complaint about a ride</option>
        <option value="refund">Refund request</option>
        <option value="lost_item">Lost item</option>
        <option value="safety_alert">Safety concern</option>
        <option value="other">Something else</option>
      </select>
      <textarea rows={2} value={f.message} onChange={set("message")}
                placeholder="Tell us what happened…"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100"/>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel}
                className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-200 px-2 py-1">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={saving}
                data-testid="katie-ticket-submit"
                className="text-[11px] font-bold uppercase tracking-widest bg-rose-500 hover:bg-rose-400 text-zinc-950 rounded px-3 py-1 disabled:opacity-50">
          {saving ? <Loader2 className="h-3 w-3 animate-spin inline"/> : "Send to support"}
        </button>
      </div>
    </div>
  );
}

function QuickBtn({ icon, label, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border transition ${
        disabled
          ? "bg-zinc-950 border-zinc-800 text-zinc-700 cursor-not-allowed"
          : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
      }`}
    >
      {icon}{label}
    </button>
  );
}

function MiniInput({ icon, ...props }) {
  return (
    <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded h-9 px-2 focus-within:border-amber-400">
      {icon && <span className="text-zinc-500">{icon}</span>}
      <input
        {...props}
        className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none min-w-0"
      />
    </div>
  );
}
